import React, { useContext } from 'react';
import { Route } from 'react-router-dom';

import {
  GQLAddress,
  GQLDeliveryUnit,
  GQLParty,
  GQLPartyType,
  GQLSecurityContext
} from '@jewelutions/core-crm-server-api-client-domain';
import { OrderDetails, OrdersList } from '../platformProducerOrders';
import { DeliveryUnitSaleCatalogList } from '../deliveryUnitSaleCatalogs/DeliveryUnitSaleCatalogList';
import { DeliveryUnitSaleCatalogDetails } from '../deliveryUnitSaleCatalogs/DeliveryUnitSaleCatalogDetails';
import PiecesList from '../pieces/PiecesList';
import PieceDetails from '../pieces/PieceDetails';
import AccessoriesList from '../accessories/AccessoriesList';
import AccessoryDetails from '../accessories/AccessoryDetails';
import AccessoryInventoryList from '../accessoriesInventory/AccessoryInventoryList';
import PieceInventoryList from '../piecesInventory/PieceInventoryList';
import FontList from '../fonts/FontList';
import FontDetails from '../fonts/FontDetails';
import SymbolCollectionList from '../symbols/SymbolCollectionList';
import SymbolCollectionDetails from '../symbols/SymbolCollectionDetails';
import { DeliveryUnitOperation } from '../../../operations/DeliveryUnitOperation';
import { useQuery } from '@apollo/client';
import { ProducerNavigation } from './ProducerNavigation';
import { ShoppingCart, Triangle } from 'react-feather';
import { RouteType } from '../../../types/routes';
import Support from '../support/Support';
import DeliveryUnitSaleCatalogPreview from '../deliveryUnitSaleCatalogs/DeliveryUnitSaleCatalogPreview/DeliveryUnitSaleCatalogPreview';
import { AuthContext } from '../../../core/AuthContext';
import { DefaultLayout } from '../../../types-hack';

interface ProducerRoutesProps {}

const ProducerRoutes: React.FC<ProducerRoutesProps> = ({}: ProducerRoutesProps) => {
  const securityContext = useContext(AuthContext).auth!;

  const party: GQLParty = {
    id: securityContext.partyId,
    name: securityContext.partyName,
    type: securityContext.partyType,
    email: securityContext.userEmail,
    address: (undefined as unknown) as GQLAddress
  };

  const { data: deliveryUnitsData } = useQuery(
    DeliveryUnitOperation.query.DeliveryUnit_getDeliveryUnitsByProducer,
    {
      variables: { producerPartyId: party?.id },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    }
  );

  const deliveryUnits: GQLDeliveryUnit[] =
    deliveryUnitsData?.DeliveryUnit_getDeliveryUnitsByProducer?.deliveryUnits || [];

  const sidebarRoutes: RouteType[] = [
    ...deliveryUnits.map(deliveryUnit => {
      return {
        id: `${deliveryUnit.name}`,
        path: '/',
        icon: <ShoppingCart />,
        userType: GQLPartyType.Producer,
        children: [
          {
            name: 'Orders',
            path: ProducerNavigation.platformProducerOrderList.pathFromParams({
              deliveryUnitId: deliveryUnit.id
            })
          },
          {
            name: 'Delivery Unit Sale Catalogs',
            path: ProducerNavigation.deliveryUnitSaleCatalogList.pathFromParams({
              deliveryUnitId: deliveryUnit.id
            })
          },
          {
            name: 'Accessories Inventory',
            path: ProducerNavigation.accessoriesInventoryList.pathFromParams({
              deliveryUnitId: deliveryUnit.id
            })
          },
          {
            name: 'Pieces Inventory',
            path: ProducerNavigation.piecesInventoryList.pathFromParams({
              deliveryUnitId: deliveryUnit.id
            })
          },
          {
            name: 'Producer designer tool',
            path: ProducerNavigation.deliveryUnitActiveSaleCatalogPreview.pathFromParams({
              deliveryUnitId: deliveryUnit.id
            })
          }
        ]
      };
    }),
    {
      id: 'Design Assets',
      path: '/',
      icon: <Triangle />,
      userType: GQLPartyType.Producer,
      children: [
        {
          name: 'Piece CADs',
          path: ProducerNavigation.pieceList.pathFromParams({})
        },
        {
          name: 'Accessory CADs',
          path: ProducerNavigation.accessoryList.pathFromParams({})
        },
        {
          name: 'Fonts',
          path: ProducerNavigation.fontList.pathFromParams({})
        },
        {
          name: 'Symbols',
          path: ProducerNavigation.symbolCollectionList.pathFromParams({})
        }
      ]
    }
  ];

  return (
    <React.Fragment>
      <Route
        path={ProducerNavigation.platformProducerOrderList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Orders"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            <OrdersList party={party} />
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.platformProducerOrderDetail.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Order Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<OrderDetails party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={ProducerNavigation.deliveryUnitSaleCatalogList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Delivery Unit Sale Catalogs"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            <DeliveryUnitSaleCatalogList party={party} />
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.deliveryUnitSaleCatalogDetail.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Delivery Unit Sale Catalog Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            <DeliveryUnitSaleCatalogDetails party={party} />
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.pieceList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Pieces"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<PiecesList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.pieceDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Piece Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<PieceDetails party={party} deliveryUnits={deliveryUnits} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.accessoryList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessories"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<AccessoriesList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.accessoryDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessory Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<AccessoryDetails party={party} deliveryUnits={deliveryUnits} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.accessoriesInventoryList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessories Inventory"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<AccessoryInventoryList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.piecesInventoryList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Pieces Inventory"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<PieceInventoryList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.fontList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Fonts"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<FontList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.fontDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Font Detail"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<FontDetails />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.symbolCollectionList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Symbols"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<SymbolCollectionList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.symbolCollectionDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Symbol Collection Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<SymbolCollectionDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.deliveryUnitActiveSaleCatalogPreview.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Delivery unit Sale Catalog Preview"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            sidebarVisible={false}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<DeliveryUnitSaleCatalogPreview party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={ProducerNavigation.support.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Support"
            sidebarRoutes={sidebarRoutes}
            helpRoute={ProducerNavigation.support.pathPattern()}
          >
            {<Support party={party} />}
          </DefaultLayout>
        )}
      />
    </React.Fragment>
  );
};

export default ProducerRoutes;
